import ScheduleEntry, { WeekConflictInfo } from '../models/ScheduleEntry';
import Teacher from '../models/Teacher';
import Group from '../models/Group';
import Subject from '../models/Subject';
import { availabilityService, type ConflictReason } from './AvailabilityService';

export type ConflictStatus = 'green' | 'orange' | 'red';

interface ConflictCalculationOptions {
  batchSize?: number;
  onProgress?: (processed: number, total: number) => void;
}

interface ConflictDependency {
  entryId: string;
  dependentEntryIds: Set<string>;
}

/**
 * ConflictManager handles conflict calculations for schedule entries
 * in an efficient, batch-oriented manner with per-week conflict tracking.
 */
class ConflictManager {
  private static instance: ConflictManager | null = null;
  
  // Cache for conflict calculations to avoid redundant work - now stores per-week conflicts
  private conflictCache = new Map<string, {
    conflictsByWeek: Map<string, WeekConflictInfo>;
    timestamp: Date;
  }>();
  
  // Track which entries depend on which other entries for conflict calculation
  private dependencyGraph = new Map<string, Set<string>>();
  
  // Default cache TTL (time to live) in milliseconds
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  static getInstance(): ConflictManager {
    if (!ConflictManager.instance) {
      ConflictManager.instance = new ConflictManager();
    }
    return ConflictManager.instance;
  }

  /**
   * Calculate conflicts for all entries in a batch
   */
  async calculateConflictsForEntries(
    entries: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[],
    options: ConflictCalculationOptions = {}
  ): Promise<ScheduleEntry[]> {
    const {
      batchSize = 50,
      onProgress
    } = options;

    console.log(`[ConflictManager] Starting batch conflict calculation for ${entries.length} entries`);
    
    // Build dependency graph first
    this.buildDependencyGraph(entries);
    
    const updatedEntries: ScheduleEntry[] = [];
    let processedCount = 0;

    // Process entries in batches to avoid blocking the UI
    for (let i = 0; i < entries.length; i += batchSize) {
      const batch = entries.slice(i, i + batchSize);
      
      // Process each entry in the batch
      for (const entry of batch) {
        const updatedEntry = this.calculateConflictForEntry(
          entry,
          entries,
          teachers,
          groups,
          subjects
        );
        updatedEntries.push(updatedEntry);
        processedCount++;
        
        // Report progress if callback provided
        if (onProgress) {
          onProgress(processedCount, entries.length);
        }
      }
      
      // Yield control to prevent blocking
      if (i + batchSize < entries.length) {
        await new Promise(resolve => setTimeout(resolve, 1));
      }
    }

    console.log(`[ConflictManager] Completed batch conflict calculation`);
    return updatedEntries;
  }

  /**
   * Calculate conflicts for a single entry across all its active weeks
   */
  calculateConflictForEntry(
    entry: ScheduleEntry,
    allEntries: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[]
  ): ScheduleEntry {
    // Skip conflict checking for events or entries without proper time/room data
    if (entry.type === 'event' || !entry.isScheduled() || !entry.isTimeValid) {
      const clonedEntry = entry.clone();
      clonedEntry.clearAllConflictStatuses();
      return clonedEntry;
    }

    // Check cache first
    const cacheKey = this.generateCacheKey(entry);
    const cached = this.conflictCache.get(cacheKey);
    
    if (cached && this.isCacheValid(cached.timestamp)) {
      const clonedEntry = entry.clone();
      // Apply cached conflicts
      cached.conflictsByWeek.forEach((conflictInfo, weekKey) => {
        clonedEntry.setConflictStatusForWeek(
          conflictInfo.week,
          conflictInfo.year,
          conflictInfo.status,
          conflictInfo.reasons
        );
      });
      return clonedEntry;
    }

    // Calculate conflicts using AvailabilityService
    try {
      const startMinutes = entry.startMinutes;
      if (startMinutes === null) {
        const clonedEntry = entry.clone();
        clonedEntry.clearAllConflictStatuses();
        return clonedEntry;
      }

      const availabilityResult = availabilityService.checkAvailability(
        entry,
        entry.room,
        entry.day,
        startMinutes,
        allEntries,
        teachers,
        groups,
        subjects
      );

      // Cache the result
      this.conflictCache.set(cacheKey, {
        conflictsByWeek: availabilityResult.conflictsByWeek,
        timestamp: new Date()
      });

      // Update the entry with conflict information
      const clonedEntry = entry.clone();
      clonedEntry.clearAllConflictStatuses();
      
      // Set conflicts for each week
      availabilityResult.conflictsByWeek.forEach((weekConflict, weekKey) => {
        clonedEntry.setConflictStatusForWeek(
          weekConflict.week,
          weekConflict.year,
          weekConflict.status,
          weekConflict.reasons
        );
      });
      
      return clonedEntry;

    } catch (error) {
      console.error(`[ConflictManager] Error calculating conflicts for entry ${entry.id}:`, error);
      const clonedEntry = entry.clone();
      clonedEntry.clearAllConflictStatuses();
      return clonedEntry;
    }
  }

  /**
   * Recalculate conflicts for specific entries and their dependencies
   */
  async recalculateConflictsForChangedEntries(
    changedEntryIds: string[],
    allEntries: ScheduleEntry[],
    teachers: Teacher[],
    groups: Group[],
    subjects: Subject[],
    options: ConflictCalculationOptions = {}
  ): Promise<ScheduleEntry[]> {
    console.log(`[ConflictManager] Recalculating conflicts for changed entries:`, changedEntryIds);

    // Find all entries that need recalculation (changed entries + their dependencies)
    const entriesToRecalculate = new Set<string>(changedEntryIds);
    
    for (const changedId of changedEntryIds) {
      const dependencies = this.dependencyGraph.get(changedId);
      if (dependencies) {
        dependencies.forEach(depId => entriesToRecalculate.add(depId));
      }
    }

    // Clear cache for entries that need recalculation
    entriesToRecalculate.forEach(entryId => {
      this.clearCacheForEntry(entryId);
    });

    // Get entries that need recalculation
    const entryMap = new Map(allEntries.map(entry => [entry.id, entry]));
    const entriesToUpdate = Array.from(entriesToRecalculate)
      .map(id => entryMap.get(id))
      .filter((entry): entry is ScheduleEntry => entry !== undefined);

    console.log(`[ConflictManager] Updating ${entriesToUpdate.length} entries due to dependencies`);

    // Recalculate conflicts for affected entries
    const updatedEntries: ScheduleEntry[] = [];
    const unchangedEntries = allEntries.filter(entry => !entriesToRecalculate.has(entry.id));

    for (const entry of entriesToUpdate) {
      const updatedEntry = this.calculateConflictForEntry(
        entry,
        allEntries,
        teachers,
        groups,
        subjects
      );
      updatedEntries.push(updatedEntry);
    }

    // Return all entries (updated + unchanged)
    return [...updatedEntries, ...unchangedEntries];
  }

  /**
   * Build dependency graph to track which entries affect each other
   * Entries that share teachers, groups, rooms, or time slots are dependent
   */
  private buildDependencyGraph(entries: ScheduleEntry[]): void {
    this.dependencyGraph.clear();

    // Initialize graph
    entries.forEach(entry => {
      this.dependencyGraph.set(entry.id, new Set());
    });

    // Build dependencies
    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const entry1 = entries[i];
        const entry2 = entries[j];

        if (entry1 && entry2 && this.areEntriesDependent(entry1, entry2)) {
          this.dependencyGraph.get(entry1.id)?.add(entry2.id);
          this.dependencyGraph.get(entry2.id)?.add(entry1.id);
        }
      }
    }

    console.log(`[ConflictManager] Built dependency graph with ${this.dependencyGraph.size} nodes`);
  }

  /**
   * Check if two entries are dependent (changes to one could affect the other's conflicts)
   */
  private areEntriesDependent(entry1: ScheduleEntry, entry2: ScheduleEntry): boolean {
    // Same room and day
    if (entry1.room === entry2.room && entry1.day === entry2.day) {
      return true;
    }

    // Same teacher
    if (entry1.teacher && entry2.teacher && entry1.teacher === entry2.teacher) {
      return true;
    }

    // Shared groups
    if (entry1.groups && entry2.groups && entry1.groups.length > 0 && entry2.groups.length > 0) {
      const groups1 = new Set(entry1.groups.map(g => typeof g === 'object' ? g.id : g));
      const groups2 = new Set(entry2.groups.map(g => typeof g === 'object' ? g.id : g));
      
      for (const group of Array.from(groups1)) {
        if (groups2.has(group)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Generate cache key for an entry's conflict calculation
   */
  private generateCacheKey(entry: ScheduleEntry): string {
    return `${entry.id}-${entry.room}-${entry.day}-${entry.timeStart}-${entry.timeEnd}-${entry.teacher}-${entry.weekSeries || ''}`;
  }

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(timestamp: Date): boolean {
    return Date.now() - timestamp.getTime() < this.CACHE_TTL;
  }

  /**
   * Clear cache for a specific entry
   */
  private clearCacheForEntry(entryId: string): void {
    const keysToDelete: string[] = [];
    Array.from(this.conflictCache.keys()).forEach(key => {
      if (key.startsWith(`${entryId}-`)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.conflictCache.delete(key));
  }

  /**
   * Clear all cached conflict calculations
   */
  clearCache(): void {
    this.conflictCache.clear();
    console.log('[ConflictManager] Cache cleared');
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number; validEntries: number } {
    const now = Date.now();
    let validEntries = 0;
    
    Array.from(this.conflictCache.values()).forEach(cached => {
      if (now - cached.timestamp.getTime() < this.CACHE_TTL) {
        validEntries++;
      }
    });

    return {
      size: this.conflictCache.size,
      validEntries
    };
  }
}

export const conflictManager = ConflictManager.getInstance();
export default ConflictManager; 